import { useMutation, useQueries, useQuery } from '@tanstack/react-query'
import { useApi } from '../../api/contexto'
import type { ErrorApi } from '../../api/errores'
import type { components } from '../../api/tipos'
import type { Historial } from './precios'

export type ListaClientesResueltos = components['schemas']['ListaClientesResueltos']
export type ClienteResuelto = components['schemas']['ClienteResuelto']
export type ListaProductos = components['schemas']['ListaProductos']
export type PaginaTabla = components['schemas']['Pagina_FilaTabla_']
export type PeticionResolver = components['schemas']['PeticionResolver']
export type RespuestaResolver = components['schemas']['RespuestaResolver']
export type PeticionPublicar = components['schemas']['PeticionPublicar']
export type RespuestaPublicar = components['schemas']['RespuestaPublicar']
export type ResultadoPublicacion = components['schemas']['ResultadoPublicacion']

/**
 * Las consultas de la pantalla de agendamiento. Viven junto a ella, como las de
 * logistica y conversaciones: ninguna otra seccion busca clientes de Odoo.
 */

/** El backend exige dos caracteres; con menos no se pregunta. */
const MINIMO_BUSQUEDA = 2

/**
 * 50 y no los 100 que admite el backend: cada fila resuelve su ficha y su
 * ultimo pedido contra Odoo, y una pagina de 100 tarda unos tres segundos. A 50
 * la espera se parte por la mitad y siguen cabiendo casi todos los clientes de
 * un dia de ruta.
 */
export const TAMANO_PAGINA = 50

/** Las que caben en el desplegable sin taparlo todo. */
const TAMANO_SUGERENCIAS = 6

export interface FiltrosTablaClientes {
  ruta: string
  semana: string
  q: string
  page: number
}

/**
 * La tabla de clientes de la pantalla: lo primero que se ve.
 *
 * `placeholderData` conserva la pagina anterior mientras llega la nueva. Sin
 * eso, cambiar de dia de ruta vaciaria la tabla durante tres segundos y las
 * casillas marcadas desaparecerian de la vista, que es justo cuando alguien
 * vuelve a marcarlas.
 */
export function useTablaClientes(filtros: FiltrosTablaClientes, activo: boolean) {
  const api = useApi()

  return useQuery<PaginaTabla, ErrorApi>({
    enabled: activo,
    queryKey: ['agendamiento', 'tabla', filtros.ruta, filtros.semana, filtros.q, filtros.page],
    queryFn: ({ signal }) =>
      api.pedir<PaginaTabla>('/api/agendamiento/tabla', {
        parametros: {
          ruta: filtros.ruta || undefined,
          semana: filtros.semana || undefined,
          q: filtros.q || undefined,
          page: filtros.page,
          page_size: TAMANO_PAGINA,
        },
        senal: signal,
      }),
    placeholderData: (previa) => previa,
    // Como el resto de la pantalla: un 503 significa que Odoo no contesta, y
    // reintentar en bucle solo retrasa el mensaje.
    retry: false,
  })
}

/**
 * Las sugerencias del buscador, por nombre y por NIF.
 *
 * Es una consulta **aparte de la tabla**, y con seis filas en vez de cincuenta,
 * porque el coste de este endpoint esta en las filas: seis vuelven casi al
 * instante y cincuenta tardan segundos. Un autocompletado que tarda tres
 * segundos no es un autocompletado.
 *
 * Respeta el dia y la semana activos a proposito: sugerir un cliente que el
 * filtro de la tabla va a dejar fuera seria mandar a alguien a una tabla vacia.
 */
export function useSugerenciasClientes(
  q: string,
  ruta: string,
  semana: string,
  activo: boolean,
) {
  const api = useApi()

  return useQuery<PaginaTabla, ErrorApi>({
    queryKey: ['agendamiento', 'sugerencias', ruta, semana, q],
    queryFn: ({ signal }) =>
      api.pedir<PaginaTabla>('/api/agendamiento/tabla', {
        parametros: {
          ruta: ruta || undefined,
          semana: semana || undefined,
          q,
          page: 1,
          page_size: TAMANO_SUGERENCIAS,
        },
        senal: signal,
      }),
    enabled: activo && q.trim().length >= MINIMO_BUSQUEDA,
    // Volver sobre lo mismo mientras se corrige una letra no vuelve a preguntar.
    staleTime: 60_000,
    retry: false,
  })
}

export function useBuscarClientes(q: string) {
  const api = useApi()

  return useQuery<ListaClientesResueltos, ErrorApi>({
    queryKey: ['agendamiento', 'clientes', q],
    queryFn: ({ signal }) =>
      api.pedir<ListaClientesResueltos>('/api/agendamiento/clientes', {
        parametros: { q, limite: 10 },
        senal: signal,
      }),
    enabled: q.trim().length >= MINIMO_BUSQUEDA,
    // Sin reintentos automaticos: un 503 significa que Odoo no contesta, y
    // reintentar en bucle solo retrasa el mensaje. El reintento lo decide el
    // operador con el boton del panel de error, como en la ultima factura.
    retry: false,
  })
}

/**
 * El catalogo entero. Cambia como mucho una vez al dia y son 89 productos, asi
 * que se cachea largo: se pide una vez y sirve para el desplegable de bonos, el
 * de extras y las dos pantallas.
 */
export function useCatalogo(activo: boolean) {
  const api = useApi()

  return useQuery<ListaProductos, ErrorApi>({
    queryKey: ['agendamiento', 'productos'],
    queryFn: ({ signal }) =>
      api.pedir<ListaProductos>('/api/agendamiento/productos', { senal: signal }),
    enabled: activo,
    staleTime: 10 * 60_000,
  })
}

/**
 * Lo que cada cliente pago la ultima vez, uno por cliente.
 *
 * En paralelo y no en una sola llamada porque el endpoint es por cliente; en
 * serie, veinte clientes serian veinte viajes a Odoo puestos en fila. Sin
 * reintentos: un 503 del ERP multiplicado por veinte filas solo retrasa la
 * pantalla, y la comparacion de precios es una ayuda, no el trabajo.
 */
export function useHistoriales(contactIds: string[]): Map<string, Historial> {
  const api = useApi()

  // `combine` y no un `Map` construido en el cuerpo del hook: TanStack Query
  // memoiza su resultado, de modo que el mapa conserva la identidad mientras
  // las respuestas no cambien. Sin eso, cada render entregaria un mapa nuevo y
  // las columnas memoizadas de la tabla se reconstruirian siempre.
  return useQueries({
    queries: contactIds.map((id) => ({
      queryKey: ['agendamiento', 'historial', id],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.pedir<Historial>(`/api/agendamiento/historial/${id}`, { senal: signal }),
      staleTime: 5 * 60_000,
      retry: false,
    })),
    combine: (resultados) => {
      const historiales = new Map<string, Historial>()
      contactIds.forEach((id, indice) => {
        const dato = resultados[indice]?.data
        if (dato) historiales.set(id, dato)
      })
      return historiales
    },
  })
}

export function useResolver() {
  const api = useApi()

  return useMutation<RespuestaResolver, ErrorApi, PeticionResolver>({
    mutationFn: (peticion) =>
      api.pedir<RespuestaResolver>('/api/agendamiento/resolver', {
        metodo: 'POST',
        cuerpo: peticion,
      }),
  })
}

/**
 * Publicar. Sin reintento automatico **a proposito**: crea presupuestos en
 * Odoo, y un reintento a espaldas del administrador podria crear el pedido dos
 * veces si el fallo fue de red y no del servidor. La idempotencia del backend
 * lo pararia, pero el reintento aqui lo decide una persona.
 */
export function usePublicar() {
  const api = useApi()

  return useMutation<RespuestaPublicar, ErrorApi, PeticionPublicar>({
    mutationFn: (peticion) =>
      api.pedir<RespuestaPublicar>('/api/agendamiento/publicar', {
        metodo: 'POST',
        cuerpo: peticion,
      }),
    retry: false,
  })
}
